// LinkMemberDialog.jsx — #57 BUILD item 3b: within-fund member identity link, client-facing with
// undo. Writes ONE link_member overlay per source stream under every non-surviving member,
// pointing target_investor at the surviving member's existing investor_name (Gate-C: same
// portal/fund only — enforced server-side too, never a new cross-fund resolver).
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { addOverlay, NOTE_MAX_LEN } from "./overlayApi";
import { collectMemberStreamIds } from "./overlayGroups";

/**
 * @param {object[]} members - selected member entries, length >= 2 (each a member node with
 *   investor_name/stream_id/positions from the SAME fund)
 */
export default function LinkMemberDialog({ open, onOpenChange, members, token, onLinked }) {
  const confirm = useConfirm();
  const [survivorIdx, setSurvivorIdx] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!members || members.length < 2) return null;

  async function handleLink(e) {
    e.preventDefault();
    const survivor = members[survivorIdx];
    const ok = await confirm({
      title: `Link ${members.length} members as "${survivor.investor_name}"?`,
      description:
        "The underlying positions never change — you can undo this later from the linked member's badge.",
      confirmText: "Link",
      cancelText: "Cancel",
    });
    if (!ok) return;

    setBusy(true);
    try {
      const targetInvestor = survivor.investor_name;
      const streamIds = members
        .filter((_, i) => i !== survivorIdx)
        .flatMap((m) => collectMemberStreamIds(m));

      if (streamIds.length === 0) {
        toast.info("Nothing to link — the other selected member(s) have no positions.");
        setBusy(false);
        return;
      }

      await Promise.all(
        streamIds.map((sid) =>
          addOverlay({
            streamId: sid,
            kind: "link_member",
            payload: { target_investor: targetInvestor },
            note,
            token,
          })
        )
      );
      toast.success(`Linked ${members.length} members as "${targetInvestor}"`);
      setNote("");
      onOpenChange(false);
      if (onLinked) onLinked();
    } catch (err) {
      toast.error("Link failed: " + (err?.message || "error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="link-member-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-gray-900">
            Link {members.length} members as the same person
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleLink} className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1 block">
              Surviving identity
            </label>
            <div className="space-y-1">
              {members.map((m, i) => (
                <label key={m.stream_id || m.investor_name} className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="radio"
                    name="link-survivor"
                    checked={survivorIdx === i}
                    onChange={() => setSurvivorIdx(i)}
                    disabled={busy}
                  />
                  {m.investor_name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1 block">
              Note (optional)
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LEN))}
              maxLength={NOTE_MAX_LEN}
              disabled={busy}
              className="text-xs min-h-[50px]"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={busy}
              className="h-8 px-3 rounded text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="h-8 px-3 rounded text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              data-testid="link-member-confirm"
            >
              {busy ? "Linking…" : "Same member"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
